/**
 * SuperDriverPanel — three lanes for a driver to become a Super Driver:
 *   1. Admin already toggled them on → shows the badge + copy (no action).
 *   2. Auto-eligibility (50+ 5-star ratings) → show Accept / Decline buttons.
 *   3. Neither → show an "Apply to become a Super Driver" button.
 *
 * State comes from `driver` prop (from GET /api/driver/me):
 *   is_super_driver, super_driver_offer_pending, super_driver_offer_reason,
 *   super_driver_application_status.
 */
import React, { useState } from 'react';
import { Zap, Loader2, Sparkles, ShieldCheck, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const SuperDriverPanel = ({ driver, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (!driver) return null;

  if (driver.is_super_driver) {
    return (
      <section
        className="mt-6 rounded-2xl p-5 text-white bg-gradient-to-r from-emerald-500 via-emerald-400 to-lime-400 shadow"
        data-testid="super-driver-active-panel"
      >
        <div className="flex items-center gap-2 font-bold uppercase text-sm tracking-wider">
          <Zap className="h-4 w-4" /> You&apos;re a Super Driver
        </div>
        <p className="text-sm text-white/90 mt-1">
          You have city-wide dispatch access and priority on stalled orders (waiting &gt;20 min).
          Keep those 5-star ratings coming!
        </p>
      </section>
    );
  }

  const apply = async () => {
    setBusy(true);
    try {
      await api.post('/driver/super-driver/apply');
      toast.success('Super Driver application submitted — ShipitNow admin will review it shortly.');
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit application');
    } finally { setBusy(false); }
  };
  const accept = async () => {
    setBusy(true);
    try {
      await api.post('/driver/super-driver/accept');
      toast.success('Welcome to the Super Driver ranks!');
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };
  const decline = async () => {
    setBusy(true);
    try {
      await api.post('/driver/super-driver/decline');
      toast('Super Driver offer dismissed.');
      onChange && (await onChange());
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  if (driver.super_driver_offer_pending) {
    return (
      <section
        className="mt-6 rounded-2xl p-5 border-2 border-lime-400 bg-lime-50"
        data-testid="super-driver-offer-panel"
      >
        <div className="flex items-center gap-2 text-emerald-900 font-bold uppercase text-sm tracking-wider">
          <Sparkles className="h-4 w-4" /> You&apos;ve unlocked Super Driver
        </div>
        <p className="text-sm text-slate-700 mt-1">
          {driver.super_driver_offer_reason || 'You crossed the 50 five-star ratings threshold.'}
          Accept to switch on city-wide dispatch and priority on stalled orders.
        </p>
        <div className="mt-3 flex items-center gap-2">
          <Button onClick={accept} disabled={busy} className="bg-lime-500 hover:bg-lime-600 text-emerald-900 font-semibold" data-testid="super-driver-accept-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
            Accept Super Driver
          </Button>
          <Button onClick={decline} disabled={busy} variant="outline" data-testid="super-driver-decline-btn">
            <X className="h-4 w-4 mr-1.5" /> Not now
          </Button>
        </div>
      </section>
    );
  }

  if (driver.super_driver_application_status === 'pending') {
    return (
      <section className="mt-6 rounded-2xl p-5 border border-slate-200 bg-slate-50" data-testid="super-driver-application-pending">
        <div className="flex items-center gap-2 text-slate-800 font-semibold">
          <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
          Super Driver application under review
        </div>
        <p className="text-sm text-slate-500 mt-1">
          ShipitNow admin will let you know as soon as they&apos;ve reviewed your application.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-6 rounded-2xl p-5 border border-slate-200 bg-white" data-testid="super-driver-apply-panel">
      <div className="flex items-center gap-2 text-slate-900 font-semibold">
        <Zap className="h-4 w-4 text-emerald-600" /> Become a Super Driver
      </div>
      <p className="text-sm text-slate-500 mt-1">
        Super Drivers see every order across their city — including stalled ones nobody else picked.
        Earn 50+ five-star ratings for automatic eligibility, or apply now and ShipitNow admin will review.
      </p>
      <div className="mt-3">
        <Button onClick={apply} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="super-driver-apply-btn">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Zap className="h-4 w-4 mr-1.5" />}
          Apply to become a Super Driver
        </Button>
      </div>
    </section>
  );
};

export default SuperDriverPanel;
